import React from 'react';
import { Check, Home, MapPin, Pencil, Plus, Printer, UserRound } from 'lucide-react';
import ActionMenu from '../ui/ActionMenu';

function StatusBadge({ value, asRide = false }) {
  const labels = asRide
    ? {
      scheduled: 'Agendada',
      completed: 'Concluída',
      cancelled: 'Cancelada',
      active: 'Ativa',
      inactive: 'Inativa',
      available: 'Disponível',
    }
    : {
      scheduled: 'Agendado',
      completed: 'Concluído',
      cancelled: 'Cancelado',
      active: 'Ativo',
      inactive: 'Inativo',
    };
  const tones = {
    scheduled: 'bg-amber-50 text-amber-700 ring-amber-200',
    completed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    cancelled: 'bg-red-50 text-red-700 ring-red-200',
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    inactive: 'bg-slate-100 text-slate-600 ring-slate-200',
    available: 'bg-sky-50 text-sky-700 ring-sky-200',
  };

  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${tones[value] || 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
      {labels[value] || value || '-'}
    </span>
  );
}

function lookupRecord(lookups, resource, id) {
  if (!id) return null;
  return (lookups[resource] || []).find((item) => String(item.id) === String(id)) || null;
}

function cityName(lookups, id) {
  return lookupRecord(lookups, 'cities', id)?.name || '-';
}

function getInitials(name) {
  if (!name) return '?';
  return String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayTime(value) {
  if (!value) return '';
  const text = String(value);
  if (/^\d{2}:\d{2}/.test(text)) return text.slice(0, 5);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function seatsUsed(boardings = []) {
  return boardings
    .filter((item) => item.status !== 'cancelled')
    .reduce((total, item) => total + 1 + (item.companion_name ? 1 : 0), 0);
}

function OccupancyBadge({ used, capacity }) {
  const max = Number(capacity) || 0;
  const full = max > 0 && used >= max;
  const almostFull = max > 0 && used / max >= 0.8;

  const className = full
    ? 'bg-red-50 text-red-700 ring-red-200'
    : almostFull
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-emerald-50 text-emerald-700 ring-emerald-200';

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${className}`}>
      {used}/{max || '-'} lugares
    </span>
  );
}

function BoardingRow({ boarding, lookups, onEdit, onDelete }) {
  const patient = lookupRecord(lookups, 'patients', boarding.patient_id);
  const facility = lookupRecord(lookups, 'facilities', boarding.facility_id);
  const name = patient?.name || `Passageiro #${boarding.patient_id || '-'}`;

  return (
    <div className="flex items-start gap-2 px-3 py-2.5">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-[11px] font-semibold text-primary">
        {getInitials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-text">{name}</span>
          {boarding.companion_name && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-muted">
              <UserRound size={10} />
              Acomp.
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] leading-snug text-muted">
          <span className="break-words">
            {cityName(lookups, boarding.destination_city_id)}
            {facility?.name ? ` · ${facility.name}` : ''}
            {boarding.appointment_time ? ` · ${displayTime(boarding.appointment_time)}` : ''}
          </span>
        </div>
        {(patient?.pickup_at_home || patient?.boarding_point) && (
          <div className={`mt-0.5 flex items-start gap-1 text-[11px] ${patient?.pickup_at_home ? 'text-primary' : 'text-muted'}`}>
            {patient?.pickup_at_home
              ? <Home size={11} className="mt-0.5 shrink-0" />
              : <MapPin size={11} className="mt-0.5 shrink-0" />}
            <span className="line-clamp-2">{patient?.boarding_point || 'Busca em casa'}</span>
          </div>
        )}
      </div>
      {(onEdit || onDelete) && <ActionMenu onEdit={onEdit} onDelete={onDelete} />}
    </div>
  );
}

export default function BoardingsByRide({
  rides,
  loading,
  loadingMore,
  hasMore,
  lookups,
  onAddBoarding,
  onEditBoarding,
  onEditRide,
  onCompleteRide,
  completingRideId,
  onDeleteBoarding,
  onPrintRoteiro,
  printingRideId,
  onLoadMore,
}) {
  if (loading && !rides.length) {
    return (
      <div className="px-5 py-12 text-center text-sm text-muted">
        Carregando corridas...
      </div>
    );
  }

  if (!rides.length) {
    return (
      <div className="px-5 py-12 text-center">
        <p className="text-sm font-medium text-text">Nenhuma corrida encontrada.</p>
        <p className="mt-1 text-sm text-muted">
          Crie uma corrida na aba Corridas para começar a adicionar embarques.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="grid justify-start gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(100%,24rem),24rem))]">
        {rides.map((ride) => {
          const boardings = ride.boardings || [];
          const driver = lookupRecord(lookups, 'drivers', ride.driver_id);
          const vehicle = lookupRecord(lookups, 'vehicles', ride.vehicle_id);
          const origin = cityName(lookups, ride.origin_city_id);
          const destination = cityName(lookups, ride.destination_city_id);
          const used = seatsUsed(boardings);
          const full = Number(ride.capacity) > 0 && used >= Number(ride.capacity);

          return (
            <article
              key={ride.id}
              className="flex min-h-[28rem] w-full flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-card"
            >
              <div className="border-b border-border bg-slate-50/80 px-4 py-3">
                <div className="flex items-start gap-2">
                  {onEditRide && (
                    <button
                      type="button"
                      onClick={() => onEditRide(ride)}
                      className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-border bg-white text-muted transition hover:border-primary hover:text-primary"
                      title="Editar corrida"
                      aria-label="Editar corrida"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {onPrintRoteiro && (
                    <button
                      type="button"
                      onClick={() => onPrintRoteiro(ride)}
                      disabled={printingRideId === ride.id}
                      className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-border bg-white text-muted transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                      title="Imprimir relação de passageiros"
                      aria-label="Imprimir relação de passageiros"
                    >
                      <Printer size={14} />
                    </button>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1.5">
                      <h3 className="min-w-0 flex-1 text-sm font-semibold leading-snug text-text">
                        <span className="break-words">{origin}</span>
                        {' → '}
                        <span className="break-words">{destination}</span>
                      </h3>
                      <StatusBadge value={ride.status} asRide />
                    </div>
                    <p className="mt-1 text-xs text-muted">{displayDate(ride.starts_at)}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <OccupancyBadge used={used} capacity={ride.capacity} />
                  <span className="truncate text-xs text-muted">{driver?.name || 'Sem motorista'}</span>
                </div>
                <p className="mt-1 truncate text-xs text-muted">
                  {vehicle ? `${vehicle.model} · ${vehicle.plate || ride.plate || '-'}` : (ride.plate || 'Sem veículo')}
                </p>
                {ride.boarding_point && (
                  <p className="mt-1 flex items-start gap-1 text-xs text-muted">
                    <MapPin size={12} className="mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{ride.boarding_point}</span>
                  </p>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {boardings.length === 0 ? (
                  <div className="flex h-full items-center justify-center px-4 text-center">
                    <p className="text-xs text-muted">Nenhum embarque nesta corrida</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {boardings.map((boarding) => (
                      <BoardingRow
                        key={boarding.id}
                        boarding={boarding}
                        lookups={lookups}
                        onEdit={() => onEditBoarding(boarding)}
                        onDelete={() => onDeleteBoarding(boarding)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-border p-3">
                {onPrintRoteiro && (
                  <button
                    type="button"
                    onClick={() => onPrintRoteiro(ride)}
                    disabled={printingRideId === ride.id}
                    className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm font-medium text-text transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Printer size={15} />
                    {printingRideId === ride.id ? 'Gerando PDF...' : 'Imprimir passageiros'}
                  </button>
                )}
                {onAddBoarding && (
                  <button
                    type="button"
                    disabled={full || ride.status === 'completed' || ride.status === 'cancelled'}
                    onClick={() => onAddBoarding(ride)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus size={15} />
                    Embarque
                  </button>
                )}
                {onCompleteRide && (
                  <button
                    type="button"
                    disabled={ride.status === 'completed' || ride.status === 'cancelled' || completingRideId === ride.id}
                    onClick={() => onCompleteRide(ride)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Check size={15} />
                    {ride.status === 'completed' ? 'Concluída' : completingRideId === ride.id ? 'Salvando...' : 'Concluir'}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {hasMore && (
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            disabled={loadingMore}
            onClick={onLoadMore}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-medium text-text transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore ? 'Carregando...' : 'Ver mais'}
          </button>
        </div>
      )}
    </div>
  );
}
